import { LAYOUT_LS_PREFIX } from '../../constants/localStorage';

import { EDITOR_LAYOUT_VERSION, TEditorLayout, TLayoutIndexEntry } from './editorLayout.types';

// Двойное подчёркивание, чтобы ключ индекса не мог столкнуться с id проекта:
// он лежит под тем же префиксом, что и блобы.
export const LAYOUT_INDEX_LS_KEY = `${LAYOUT_LS_PREFIX}__index`;

export const LAYOUT_PROJECTS_LIMIT = 20;

const blobKey = (projectId: string) => `${LAYOUT_LS_PREFIX}${projectId}`;

// localStorage может бросить где угодно: приватный режим Safari, отключённые куки,
// переполненная квота. Для настроек UI любая такая ошибка — не повод падать.
function readRaw(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeRaw(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function removeRaw(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Нечего делать: хранилище недоступно.
  }
}

function parseJson(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

function isEntityRef(value: unknown): boolean {
  if (value === null) {
    return true;
  }

  if (!isRecord(value)) {
    return false;
  }

  return typeof value.id === 'string' && (value.type === 'note' || value.type === 'collection');
}

// «Конечное число» — слишком слабо: x = 1e308 уносит окно туда, откуда его не
// вернуть мышью, а нулевая или отрицательная ширина не имеет смысла. Границы взяты
// с большим запасом относительно любого реального экрана.
const MAX_COORD = 100_000;
const MIN_SIDE = 1;
const MAX_SIDE = 20_000;

const inRange = (value: unknown, min: number, max: number): boolean =>
  isFiniteNumber(value) && value >= min && value <= max;

function isWindowSettings(value: unknown): boolean {
  if (!isRecord(value)) {
    return false;
  }

  const { isOpen, coords, size, scrollPercent } = value;

  if (typeof isOpen !== 'boolean') {
    return false;
  }

  if (
    !isRecord(coords) ||
    !inRange(coords.x, -MAX_COORD, MAX_COORD) ||
    !inRange(coords.y, -MAX_COORD, MAX_COORD)
  ) {
    return false;
  }

  if (
    !isRecord(size) ||
    !inRange(size.width, MIN_SIDE, MAX_SIDE) ||
    !inRange(size.height, MIN_SIDE, MAX_SIDE)
  ) {
    return false;
  }

  return inRange(scrollPercent, 0, 1);
}

function isContentWindows(value: unknown): boolean {
  if (!isRecord(value)) {
    return false;
  }

  const { list, settings } = value;

  if (!Array.isArray(list) || !list.every((id) => typeof id === 'string')) {
    return false;
  }

  if (new Set(list as string[]).size !== list.length) {
    return false;
  }

  if (!isRecord(settings)) {
    return false;
  }

  return Object.values(settings).every(isWindowSettings);
}

/**
 * Единственная граница доверия к содержимому хранилища.
 *
 * `JSON.parse` + сверка версии её не образуют: блоб можно поправить руками из
 * девтулзов, а старая версия приложения могла записать другую форму. Пропущенный
 * `NaN` в геометрии уедет в `Rnd`, а осиротевший `settings` уронит `WindowTabsPanel`.
 * Дальше по коду `TEditorLayout` считается корректным.
 */
function isEditorLayout(value: unknown): value is TEditorLayout {
  if (!isRecord(value)) {
    return false;
  }

  if (value.v !== EDITOR_LAYOUT_VERSION || !isFiniteNumber(value.savedAt)) {
    return false;
  }

  if (!isEntityRef(value.selected) || !isEntityRef(value.secondarySelected)) {
    return false;
  }

  if (typeof value.isCompiled !== 'boolean' || typeof value.isSecondaryCompiled !== 'boolean') {
    return false;
  }

  return isContentWindows(value.contentWindows);
}

// Все id проектов, для которых в хранилище реально лежит блоб.
function scanStoredProjectIds(): string[] {
  try {
    return Object.keys(localStorage)
      .filter((key) => key.startsWith(LAYOUT_LS_PREFIX) && key !== LAYOUT_INDEX_LS_KEY)
      .map((key) => key.slice(LAYOUT_LS_PREFIX.length));
  } catch {
    return [];
  }
}

function parseIndex(): TLayoutIndexEntry[] {
  const raw = readRaw(LAYOUT_INDEX_LS_KEY);

  if (!raw) {
    return [];
  }

  const parsed = parseJson(raw);

  if (!Array.isArray(parsed)) {
    return [];
  }

  return parsed.filter(
    (entry): entry is TLayoutIndexEntry =>
      isRecord(entry) && typeof entry.id === 'string' && isFiniteNumber(entry.savedAt),
  );
}

/**
 * Индекс, достроенный до реального содержимого хранилища.
 *
 * Индекс и блобы — две независимые записи, поэтому они умеют разъезжаться: битый
 * индекс, отказ второй записи, ручная правка. Без досборки потерянные из индекса
 * блобы становились бы вечными сиротами — никогда не вытеснялись и потихоньку
 * съедали квоту. Досборка делает инвариант самовосстанавливающимся.
 */
function readIndex(): TLayoutIndexEntry[] {
  const entries = parseIndex();
  const known = new Set(entries.map((entry) => entry.id));

  // savedAt = 0 ставит найденную сироту в начало очереди на вытеснение: когда её
  // блоб прочитают, он сам обновит запись честным временем.
  const orphans = scanStoredProjectIds()
    .filter((id) => !known.has(id))
    .map((id) => ({ id, savedAt: 0 }));

  return [...entries, ...orphans];
}

/**
 * Держит число сохранённых проектов в пределах лимита.
 *
 * Индекс и блоб — две независимые записи, транзакции между ними нет. Поэтому здесь
 * нет отката: записи, для которых блоба уже нет, просто выбрасываются из индекса,
 * а не считаются занятыми слотами.
 */
function updateIndex(projectId: string, savedAt: number): void {
  const entries = readIndex()
    .filter((entry) => entry.id !== projectId)
    .filter((entry) => readRaw(blobKey(entry.id)) !== null);

  entries.push({ id: projectId, savedAt });
  entries.sort((a, b) => a.savedAt - b.savedAt);

  while (entries.length > LAYOUT_PROJECTS_LIMIT) {
    const evicted = entries.shift();

    if (evicted) {
      removeRaw(blobKey(evicted.id));
    }
  }

  writeRaw(LAYOUT_INDEX_LS_KEY, JSON.stringify(entries));
}

export function loadLayout(projectId: string): TEditorLayout | null {
  const raw = readRaw(blobKey(projectId));

  if (!raw) {
    return null;
  }

  const parsed = parseJson(raw);

  if (!isEditorLayout(parsed)) {
    // Мусор вычищаем сразу, чтобы он не пережил ещё одну сессию.
    removeRaw(blobKey(projectId));
    return null;
  }

  return parsed;
}

export function saveLayout(projectId: string, layout: TEditorLayout): void {
  // Порядок важен: сначала блоб, потом индекс. Сбой на втором шаге оставит
  // неучтённый блоб (данные целы), а не индекс, ссылающийся в пустоту.
  if (!writeRaw(blobKey(projectId), JSON.stringify(layout))) {
    return;
  }

  updateIndex(projectId, layout.savedAt);
}

export function dropLayout(projectId: string): void {
  removeRaw(blobKey(projectId));

  const entries = readIndex().filter((entry) => entry.id !== projectId);

  writeRaw(LAYOUT_INDEX_LS_KEY, JSON.stringify(entries));
}
